/**
 * Agent · Setup (phase A). Compliance sees every Watch rule with its switch
 * and threshold, the last run, what is open and what came and went in the
 * last seven days, and the recent runs of the hourly job. Nothing here is
 * shown to anyone else; cards arrive in phase B. House layout: white cards,
 * slate text, one orange, no dark badges.
 */
import React, { useState } from 'react';
import { api, fmtWhen } from './api.js';

function Card({ title, children, right, className = '' }) {
  return (
    <section className={`bg-white rounded-2xl border border-slate-200 ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {right}
        </div>
      )}
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function Button({ children, onClick, kind = 'primary', disabled, title }) {
  const styles = {
    primary: 'bg-orange-600 text-white hover:bg-orange-700 shadow-sm',
    secondary: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50',
  };
  return <button type="button" onClick={onClick} disabled={disabled} title={title} className={`px-4 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${styles[kind]}`}>{children}</button>;
}

function Stat({ label, value, tone = 'text-slate-800' }) {
  return (
    <div className="min-w-[88px]">
      <div className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function RuleRow({ rule, onChange, busy }) {
  const [threshold, setThreshold] = useState(rule.threshold ?? '');
  const dirty = rule.threshold_label && Number(threshold) !== Number(rule.threshold);
  return (
    <div className={`py-4 border-b border-slate-100 last:border-0 ${rule.enabled ? '' : 'opacity-60'}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-slate-800">{rule.title}</h4>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${rule.enabled ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}`}>{rule.enabled ? 'On' : 'Off'}</span>
          </div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{rule.what}</p>
          <p className="text-xs text-slate-400 mt-1">Reads {rule.sources.join(', ')}. {rule.last_run_at ? `Last run ${fmtWhen(rule.last_run_at)}${rule.last_duration_ms != null ? ` in ${rule.last_duration_ms} ms` : ''}.` : 'Has not run yet.'}</p>
        </div>
        <div className="flex items-end gap-5 flex-wrap">
          <Stat label="Open" value={rule.open} />
          <Stat label="Past due" value={rule.past_due} tone={rule.past_due > 0 ? 'text-rose-700' : 'text-slate-800'} />
          <Stat label="No owner" value={rule.without_owner} tone={rule.without_owner > 0 ? 'text-orange-700' : 'text-slate-800'} />
          <Stat label="New, 7 days" value={rule.new_7d} />
          <Stat label="Resolved, 7 days" value={rule.resolved_7d} />
        </div>
      </div>
      {rule.cards && (
        <div className="flex items-end gap-5 flex-wrap mt-3 pt-3 border-t border-dashed border-slate-100">
          <Stat label="Cards, 7 days" value={rule.cards.shown_7d} />
          <Stat label="Opened" value={rule.cards.opened_7d} />
          <Stat label="Yes" value={rule.cards.approved_7d} />
          <Stat label="Waited" value={rule.cards.snoozed_7d} />
          <Stat label="Dismissed" value={rule.cards.dismissed_7d} tone={rule.cards.dismiss_rate_7d != null && rule.cards.dismiss_rate_7d > 50 ? 'text-rose-700' : 'text-slate-800'} />
          <Stat label="Asked" value={rule.cards.asked_7d} />
          <Stat label="Dismiss rate, 30 days" value={rule.cards.dismiss_rate_30d != null ? `${rule.cards.dismiss_rate_30d}%` : 'n/a'} tone={rule.cards.dismiss_rate_30d != null && rule.cards.dismiss_rate_30d > 50 ? 'text-rose-700' : 'text-slate-800'} />
        </div>
      )}
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        {rule.threshold_label && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="number" min={0} max={365} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-sm" aria-label={`${rule.title} threshold`} />
            <span className="text-slate-500">{rule.threshold_label}</span>
            {dirty && <Button kind="secondary" disabled={busy} onClick={() => onChange(rule.key, { threshold: Number(threshold) })}>Save</Button>}
          </label>
        )}
        <Button kind="secondary" disabled={busy} onClick={() => onChange(rule.key, { enabled: !rule.enabled })}>{rule.enabled ? 'Switch off' : 'Switch on'}</Button>
      </div>
    </div>
  );
}

export default function SetupTab({ data, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const change = async (key, body) => {
    setBusy(true); setMsg(null);
    const d = await api(`/setup/rules/${key}`, { method: 'PATCH', body });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not save'); return; }
    setMsg('Saved.'); onRefresh?.();
  };
  const runNow = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/setup/run-now', { method: 'POST' });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not run'); return; }
    setMsg(`Ran in ${d.ms} ms: ${d.totals.open} open, ${d.totals.new} new, ${d.totals.resolved} resolved.`); onRefresh?.();
  };
  const nudgeNow = async (ignoreQuiet) => {
    setBusy(true); setMsg(null);
    const d = await api('/setup/nudge-now', { method: 'POST', body: { ignore_quiet: ignoreQuiet === true } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not run'); return; }
    const t = d.totals;
    setMsg(`Nudge ran in ${d.ms} ms: ${t.cards} ${t.cards === 1 ? 'card' : 'cards'} for ${t.people} ${t.people === 1 ? 'person' : 'people'} (${t.judged} judged, ${t.templated} by template), ${t.held} held, ${t.said_once} already said, ${t.over_cap} over the cap, ${t.waiting} waiting for a second signal${t.quiet ? ', quiet hours' : ''}.`); onRefresh?.();
  };
  const setAudience = async (audience) => {
    setBusy(true); setMsg(null);
    const d = await api('/setup/audience', { method: 'PATCH', body: { audience } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not save'); return; }
    setMsg(audience === 'everyone' ? 'The agent now speaks to everyone in the organization.' : 'The agent now speaks to the compliance team only.'); onRefresh?.();
  };
  const setDigest = async (on) => {
    setBusy(true); setMsg(null);
    const d = await api('/setup/digest', { method: 'PATCH', body: { on } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not save'); return; }
    setMsg(on ? 'The compliance team receives the brief by email at 6 AM.' : 'The digest is off.'); onRefresh?.();
  };
  const retire = async (key) => {
    setBusy(true); setMsg(null);
    const d = await api(`/setup/rules/${key}`, { method: 'PATCH', body: { enabled: false, reason: 'retired' } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not retire the rule'); return; }
    setMsg('The rule is retired. It can be switched on again below.'); onRefresh?.();
  };
  const disputeFact = async (id) => {
    const reason = window.prompt('Why is this fact wrong? It is removed and will not be written again.');
    if (!reason) return;
    setBusy(true); setMsg(null);
    const d = await api(`/memory/${id}/dispute`, { method: 'POST', body: { reason } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not dispute'); return; }
    setMsg('The fact was removed and blocked.'); onRefresh?.();
  };
  const { status, rules, runs, pool, spend = [], deployment, cards, tuning = [], rollout = null, memory = { facts: [], blocked: 0 }, actions_30d = 0, coverage = [], ownership = null } = data;
  const spendTotal = spend.reduce((n, s) => n + Number(s.cost_usd || 0), 0);
  const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
  return (
    <div className="space-y-6">
      <Card title="Where the agent stands" right={<div className="flex gap-2 flex-wrap"><Button kind="secondary" onClick={runNow} disabled={busy || !status.watch || !pool.ok} title={!pool.ok ? pool.reason || 'The agent database connection is not configured' : 'Run every rule for this organization now'}>Run the Watch now</Button><Button onClick={() => nudgeNow(false)} disabled={busy || !status.nudge || !pool.ok} title="Compose cards for this organization now, within the quiet rules">Run the Nudge now</Button><Button kind="secondary" onClick={() => nudgeNow(true)} disabled={busy || !status.nudge || !pool.ok} title="Compose now even outside speaking hours (the caps still apply)">Ignore quiet hours</Button></div>}>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Switched on</dt><dd className="text-slate-800 mt-0.5">{status.enabled ? 'Yes, for this organization' : 'No'}</dd></div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">Who it speaks to</dt>
            <dd className="text-slate-800 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{status.audience === 'everyone' ? 'Everyone in the organization' : 'The compliance team only'}</span>
              <button type="button" disabled={busy} onClick={() => setAudience(status.audience === 'everyone' ? 'compliance_only' : 'everyone')} className="text-[12px] text-[#E5601F] hover:underline disabled:opacity-50">{status.audience === 'everyone' ? 'Limit to compliance' : 'Widen to everyone'}</button>
            </dd>
          </div>
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">The hourly Watch</dt><dd className={`mt-0.5 ${pool.ok ? 'text-slate-800' : 'text-orange-800'}`}>{status.watch && pool.ok ? 'Running through the read-only agent connection' : (pool.reason || 'Not running')}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">The Judge</dt><dd className="text-slate-800 mt-0.5">{!status.nudge ? 'Cards are off on this container' : status.judge ? `On, ${deployment || 'gpt-5 mini'} inside Azure, US` : 'Off, cards use the set templates'}</dd></div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">The 6 AM digest</dt>
            <dd className="text-slate-800 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{status.digest ? 'The brief goes to the compliance team by email' : 'Off, the brief lives on the Brief tab'}</span>
              <button type="button" disabled={busy} onClick={() => setDigest(!status.digest)} className="text-[12px] text-[#E5601F] hover:underline disabled:opacity-50">{status.digest ? 'Switch off' : 'Switch on'}</button>
            </dd>
          </div>
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Cards, 7 days</dt><dd className="text-slate-800 mt-0.5">{cards?.cards_7d ?? 0} for {cards?.people_7d ?? 0} {cards?.people_7d === 1 ? 'person' : 'people'}, {cards?.judge_7d ?? 0} by the Judge</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Spend, 14 days</dt><dd className="text-slate-800 mt-0.5">${spendTotal.toFixed(2)} across {spend.reduce((n, s) => n + (s.calls || 0), 0)} calls</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Quiet hours</dt><dd className="text-slate-800 mt-0.5">7 AM to 6 PM, {status.time_zone}, weekdays</dd></div>
        </dl>
        {msg && <p className="text-sm text-slate-700 mt-4">{msg}</p>}
      </Card>

      <Card title="Weekly tuning" right={<span className="text-xs text-slate-500">dismiss rate over 30 days, at least four decisions</span>}>
        {tuning.length === 0 ? <p className="text-sm text-slate-600">No rule is dismissed more than half the time. Nothing to retire this week.</p> : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">These rules are dismissed more than half the time. Retiring one switches it off for this organization and records why; it can be switched on again under Rules.</p>
            {tuning.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-3 flex-wrap py-2 border-t border-slate-100">
                <div><p className="text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-500">Dismissed {t.dismiss_rate_30d}% of {t.decided_30d} decisions in 30 days</p></div>
                <Button kind="secondary" disabled={busy} onClick={() => retire(t.key)}>Retire this rule</Button>
              </div>
            ))}
          </div>
        )}
        {rollout && (
          <p className="text-sm text-slate-600 mt-4 pt-3 border-t border-slate-100">
            Rollout check: {rollout.days_observed} {rollout.days_observed === 1 ? 'day' : 'days'} of cards observed, {rollout.uncited_30d} {rollout.uncited_30d === 1 ? 'card' : 'cards'} without a cited signal in 30 days, {actions_30d} {actions_30d === 1 ? 'action run' : 'actions run'}. {rollout.ready ? 'The thirty-day condition is met; widening to everyone is a compliance decision, taken above.' : 'Widening to everyone waits for thirty clean days.'}
          </p>
        )}
      </Card>

      <Card title="What the agent remembers" right={<span className="text-xs text-slate-500">{memory.facts.length} in force · {memory.blocked} blocked</span>}>
        <p className="text-sm text-slate-600 mb-2 leading-relaxed">Every fact names the row it was read from and expires after 180 days unless seen again. The Judge writes at most three per brief. A disputed fact is removed at once and the same words about the same subject are blocked.</p>
        {memory.facts.length === 0 ? <p className="text-sm text-slate-500">Nothing remembered yet.</p> : (
          <div className="divide-y divide-slate-100">
            {memory.facts.map((f) => (
              <div key={f.id} className="py-2 flex items-start justify-between gap-3 flex-wrap text-sm">
                <div className="min-w-0 flex-1"><p className="text-slate-800">{f.fact}</p><p className="text-xs text-slate-500">{f.subject_type} {f.subject_id} · from a {f.source_kind} ({f.source_id}) · by {f.created_by === 'agent' ? 'the agent' : 'compliance'} · seen {fmtDay(f.last_observed_at)} · until {fmtDay(f.expires_at)}</p></div>
                <button type="button" disabled={busy} onClick={() => disputeFact(f.id)} className="text-[12.5px] text-[#E5601F] hover:underline disabled:opacity-50">Dispute</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Coverage by app" right={<span className="text-xs text-slate-500">{coverage.filter((c) => c.rules.length).length} apps watched · {coverage.filter((c) => !c.rules.length).length} outside</span>}>
        <p className="text-sm text-slate-600 mb-3 leading-relaxed">Which rule watches which app, and why an app is outside. Notices go only to the accountable person; an item with nobody named, or more than one, goes to the compliance officer in one ownership notice, and the person named afterwards is the only one notified from then on.</p>
        {ownership && (
          <p className="text-sm text-slate-700 mb-3 pb-3 border-b border-slate-100">Ownership queue: {ownership.unowned} {ownership.unowned === 1 ? 'open item has' : 'open items have'} no accountable person, {ownership.declined} {ownership.declined === 1 ? 'was' : 'were'} declined by the person named, {ownership.assigned_30d} {ownership.assigned_30d === 1 ? 'assignment' : 'assignments'} recorded in 30 days.</p>
        )}
        <div className="divide-y divide-slate-100" data-coverage>
          {coverage.map((c) => (
            <div key={c.tile} className="py-2 flex items-start justify-between gap-4 flex-wrap text-sm">
              <div className="min-w-0 flex-1">
                <p className="text-slate-800 font-medium">{c.app}</p>
                <p className="text-xs text-slate-500">{c.rules.length ? c.rule_titles.join(' · ') : c.excluded}{c.note ? `. ${c.note}` : ''}</p>
              </div>
              {c.rules.length ? <span className="text-xs text-slate-500 tabular-nums">{c.open} open{c.without_owner ? `, ${c.without_owner} without an owner` : ''}</span> : <span className="text-[11px] uppercase tracking-wide text-slate-400">Outside</span>}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Rules" right={<span className="text-xs text-slate-500">{rules.length} rules, plain SQL, no model</span>}>
        <p className="text-sm text-slate-600 mb-2 leading-relaxed">Each rule reads only the agent views of its app and turns what it finds into a signal, one per situation and person. A signal stays open while the condition holds and is resolved the first run it no longer does. The second line under each rule counts the notices it produced and what people did with them; a rule dismissed more than half the time is marked for tuning.</p>
        {rules.map((r) => <RuleRow key={r.key} rule={r} onChange={change} busy={busy} />)}
      </Card>

      <Card title="Spend per day" right={<span className="text-xs text-slate-500">from the usage log, list prices, the Judge only</span>}>
        {spend.length === 0 ? <p className="text-sm text-slate-500">No model calls in the last fourteen days.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500"><th className="py-1.5 pr-4">Day</th><th className="py-1.5 pr-4">Calls</th><th className="py-1.5 pr-4">Tokens</th><th className="py-1.5 pr-4">Errors</th><th className="py-1.5">Cost</th></tr></thead>
            <tbody>{spend.map((s) => <tr key={String(s.day)} className="border-t border-slate-100"><td className="py-1.5 pr-4 text-slate-700">{String(s.day).slice(0, 10)}</td><td className="py-1.5 pr-4 tabular-nums">{s.calls}</td><td className="py-1.5 pr-4 tabular-nums">{Number(s.tokens).toLocaleString()}</td><td className="py-1.5 pr-4 tabular-nums">{s.errors}</td><td className="py-1.5 tabular-nums">${Number(s.cost_usd).toFixed(4)}</td></tr>)}</tbody>
          </table>
        )}
      </Card>

      <Card title="Recent runs of the hourly jobs">
        {runs.length === 0 ? <p className="text-sm text-slate-500">No runs recorded yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500"><th className="py-1.5 pr-4">Started</th><th className="py-1.5 pr-4">Job</th><th className="py-1.5 pr-4">Outcome</th><th className="py-1.5 pr-4">Organizations</th><th className="py-1.5 pr-4">Open / cards</th><th className="py-1.5 pr-4">New / people</th><th className="py-1.5 pr-4">Resolved / held</th><th className="py-1.5">Duration</th></tr></thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 pr-4 text-slate-700">{fmtWhen(r.started_at)}</td>
                  <td className="py-1.5 pr-4 text-slate-700">{r.job === 'agent_nudge' ? 'Nudge' : 'Watch'}</td>
                  <td className="py-1.5 pr-4 text-slate-700">{r.skipped ? 'Another replica ran it' : r.ok ? 'Completed' : `Failed: ${r.error || ''}`}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{r.counts?.organizations ?? ''}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{r.counts?.open ?? r.counts?.cards ?? ''}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{r.counts?.new ?? r.counts?.people ?? ''}</td>
                  <td className="py-1.5 pr-4 tabular-nums">{r.counts?.resolved ?? r.counts?.held ?? ''}</td>
                  <td className="py-1.5 tabular-nums">{r.counts?.duration_ms != null ? `${r.counts.duration_ms} ms` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
